import { Box, Grid, Stack, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import StyledSelectField from "../../../ui/styledSelectField";
import AsyncSelect from "react-select/async";
import InputField from "../../../ui/styledInput";
import StyledButton from "../../../ui/styledButton";
import ProgressBar from "../../../ui/ProgressBar";
import { Controller, useForm } from "react-hook-form";
import FileUpload from "../../../utils/FileUpload";
import StyledTextArea from "../../../ui/styledTextArea";
import { toast } from "react-toastify";
import { useUserSuggestionList } from "../../../hooks/queries/useUser";
import { useSendBulkPushNotification } from "../../../hooks/mutations/useNotificationMutation";
import { useImageUpload } from "../../../hooks/mutations/useImageUpload";

export default function EmailNotification() {
  const [userList, setUserList] = useState([]);
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [selectedFile, setSelectedFile] = useState();
  const [suggestionQuery, setSuggestionQuery] = useState("");
  const reference = useRef();
  
  // Use mutation hooks
  const sendBulkPushNotificationMutation = useSendBulkPushNotification();
  const imageUploadMutation = useImageUpload();
  
  // Use query hook for user suggestions
  const { data: suggestionData } = useUserSuggestionList(suggestionQuery, !!suggestionQuery);
  
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      sendTo: [],
      subject: "",
      content: "",
    },
  });
  const onSubmit = async(data) => {
    const mails = data.sendTo.map((dt) => dt.value);
    const userIds = data.sendTo.map(dataItem => {
      const user = userList.find(user => user.mobile === dataItem.label);
      return user ? user._id : null;
    }).filter(id => id !== null); 
    
    const formDataObject = {
      to: mails,
      subject: data.subject,
      text: data.content,
      url: data.url,
      userArray: userIds,
    };

    if (selectedFile) {
      imageUploadMutation.mutate(selectedFile, {
        onSuccess: (uploadImage) => {
          formDataObject.imagePath = uploadImage.url;
          sendBulkPushNotificationMutation.mutate(formDataObject, {
            onSuccess: (res) => {
              toast.success("Send successfully");
              reset();
              setSelectedFile(null);
            },
            onError: (error) => {
              toast.error(error?.response?.data?.error || "Failed to send notification");
            }
          });
        },
        onError: (error) => {
          toast.error("Image upload error");
        }
      });
    } else {
      sendBulkPushNotificationMutation.mutate(formDataObject, {
        onSuccess: (res) => {
          toast.success("Send successfully");
          reset();
        },
        onError: (error) => {
          toast.error(error?.response?.data?.error || "Failed to send notification");
        }
      });
    }
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file.files[0]);
    let i = 0;
    let st = setInterval(() => {
      if (i === 90) {
        clearInterval(st);
      }
      i = i + 10;
      setUploadPercentage(i);
    }, 40);
  };

  const loadUserOptions = async (inputValue) => {
    setSuggestionQuery(inputValue);
    
    if (suggestionData?.result) {
      const mappedUsers = suggestionData.result.map((user) => ({
        label: user.mobile,
        value: user.firebaseToken,
      }));
      
      // Adding the 'All' option to the beginning of the mappedUsers array
      const updatedSuggestions = [
        { label: 'All', value: '*' },
        ...mappedUsers,
      ];
      
      setUserList(suggestionData.result);
      return updatedSuggestions;
    }
    return [];
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          borderRadius: "8px",
          px: { md: 30, xs: 2 },
          py: 5,
          alignItems: "center",
        }}
      >
        <Stack direction={"column"} spacing={2}>
          <Box sx={BoxStyle}>
            <Typography sx={HeadingStyle}>Create Push Notifications</Typography>
            <Typography sx={TextStyle}>Send To</Typography>
            <Controller
              name="sendTo"
              control={control}
              render={({ field }) => (
                <>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={loadUserOptions}
                    placeholder="Select User"
                    isMulti
                    {...field}
                    styles={customStyles}
                    theme={customTheme}
                    onChange={(selectedOptions) => {
                      if (
                        selectedOptions.some((option) => option.value === "*")
                      ) {
                        const confirmation = window.confirm("Are you sure want to send notification to all?");
                        if(confirmation){
                          setSuggestionQuery("");
                          // Wait for the data to update, then map it
                          setTimeout(() => {
                            if (suggestionData?.result) {
                              const mappedUsers = suggestionData.result.map((user) => ({
                                label: user.mobile,
                                value: user.firebaseToken,
                              }));
                              setValue("sendTo", mappedUsers);
                            }
                          }, 100);
                        }
                      } else {
                        setValue("sendTo", selectedOptions);
                      }
                    }}
                  />
                  {errors.sendTo && (
                    <span style={errorMessageStyle}>
                      {errors.sendTo.message}
                    </span>
                  )}
                </>
              )}
              rules={{ required: "User is required" }}
            />
            <Typography sx={TextStyle}>Subject</Typography>
            <Controller
              name="subject"
              control={control}
              render={({ field }) => (
                <>
                  <InputField placeholder={"Enter Subject line"} {...field} />
                  {errors.subject && (
                    <span style={errorMessageStyle}>
                      {errors.subject.message}
                    </span>
                  )}
                </>
              )}
              rules={{ required: "Subject is required" }}
            />
            <Typography sx={TextStyle}>Content</Typography>
            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <>
                  <StyledTextArea
                    placeholder={"Add message"}
                    height={"153px"}
                    specialAlign={true}
                    {...field}
                  />{" "}
                  {errors.content && (
                    <span style={errorMessageStyle}>
                      {errors.content.message}
                    </span>
                  )}
                </>
              )}
              rules={{ required: "Content is required" }}
            />

            <FileUpload
              ref={reference}
              accept={"*"}
              removedFile={selectedFile ? false : true}
              onFileSelect={handleFileSelect}
            />
            {selectedFile && (
              <ProgressBar
                UploadProgress={uploadPercentage}
                filename={selectedFile.name}
                onClose={() => {
                  setSelectedFile();
                }}
              />
            )} 

            {/* <Typography sx={TextStyle}>Target Url</Typography>
            <Controller
              name="url"
              control={control}
              render={({ field }) => (
                <>
                  <InputField placeholder={"Enter Url"} {...field} />
                  {errors.subject && (
                    <span style={errorMessageStyle}>
                      {errors.subject.message}
                    </span>
                  )}
                </>
              )}
            /> */}
            <StyledButton
              type="submit"
              variant={"primary"}
              width="316"
              height="46"
              style={{ borderRadius: "8px" }}
            >
              Send
            </StyledButton>
          </Box>
        </Stack>
      </Box>
    </form>
  );
}
const TextStyle = {
  color: "var(--white, #F7F8FC)",
  textAlign: "start",
  width: "100%",
  fontFamily: "Inter",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: "700",
  lineHeight: "normal",
  letterSpacing: "0.3px",
  textTransform: "capitalize",
};
const BoxStyle = {
  display: "flex",
  padding: "30px 20px",
  flexDirection: "column",
  alignItems: "center",
  gap: "17px",
  borderRadius: "var(--borderRadius, 4px)",
  background: "#1c1d22",
  width: "356x",
};

const HeadingStyle = {
  color: "var(--Grey, #B5B8C5)",
  textAlign: "center",
  fontFamily: "Inter",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: "700",
  lineHeight: "normal",
  letterSpacing: "0.3px",
};
const errorMessageStyle = {
  color: "red",
};

// customStyles for react-select/AsyncSelect
const customStyles = {
  control: (provided, state) => ({
    ...provided,
    padding: "8px",
    border: "1px solid rgba(255, 255, 255, 0.20)",
    borderRadius: "4px",
    backgroundColor: state.isFocused ? "#39383D" : "#39383D",
    color: state.isFocused ? "#fff" : "#B5B8C5",
    boxShadow: state.isFocused ? "0 0 0 2px #fff" : "none",
    cursor: "pointer",
    height: "55px", // Set this to the height of your input
    overflow: "hidden", // Hide the overflow
  }),
  input: (base) => ({
    ...base,
    color: "#fff",
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: "none",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#242424" : "#39383D",
    color: state.isFocused ? "#fff" : "#B5B8C5",
    cursor: "pointer",
    ":active": {
      backgroundColor: "#242424",
    },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#39383D",
    color: "#B5B8C5",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#F7F8FC",
  }),
};

// customTheme for react-select/AsyncSelect
const customTheme = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: "#39383D", // primary color for the select, used for the focused state
    // Add other color overrides if necessary
  },
});
